import { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { fmtInvoiceDate } from "./OnlineInvoiceDetailModal";
import { ClockCounterClockwise, ChatCircleText, EnvelopeSimple, ArrowRight, CurrencyDollar, NotePencil, CheckCircle, XCircle } from "@phosphor-icons/react";
import { PROBLEMS } from "../data/repairCatalog";

const KIND_LABELS = {
  ticket_booked: "Repair booked in", ticket_parts_ordered: "Part ordered", parts_ordered: "Part ordered",
  ticket_parts_arrived: "Part arrived", parts_arrived: "Part arrived", ticket_diagnosis_ready: "Diagnosis ready",
  diagnosis_ready: "Diagnosis ready", ticket_ready_pickup: "Ready for pickup", ready_for_pickup: "Ready for pickup",
  ticket_overdue_pickup: "Pickup reminder", overdue_pickup: "Pickup reminder", ticket_update: "Ticket update",
  receipt: "Receipt", invoice_sent: "Invoice", invoice_paid: "Paid receipt", stripe_link: "Payment link",
  stripe_deposit: "Deposit link", stripe_reminder: "Payment reminder", stripe_payment_received: "Payment received",
  refund_instore: "Refund confirmation", refund_card: "Card refund confirmation", review_request: "Review request",
};
const STATUS = {
  sent: "bg-emerald-50 border-emerald-200 text-emerald-700",
  failed: "bg-rose-50 border-rose-200 text-rose-700",
  not_configured: "bg-amber-50 border-amber-200 text-amber-700",
};
const fmt = (v) => (v == null ? "—" : `$${money(v)}`);

function PriceRow({ p }) {
  const up = p.new_total >= p.old_total;
  return (
    <li className="px-4 py-3" data-testid="history-price-row">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">
            <CurrencyDollar size={14} weight="bold" className="text-[#1E5FEB]" />
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold text-slate-900 flex items-center gap-1.5 tabular-nums">
              {fmt(p.old_total)} <ArrowRight size={12} weight="bold" className="text-slate-400" />
              <span className={up ? "text-amber-700" : "text-emerald-700"}>{fmt(p.new_total)}</span>
            </div>
            <div className="text-[11px] text-slate-500">{fmtInvoiceDate(p.at)} · {p.by}</div>
          </div>
        </div>
        <span className="shrink-0 h-6 px-2 rounded-md border border-slate-200 bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-600 inline-flex items-center">
          {p.source === "ticket" ? "Ticket edit" : "Invoice edit"}
        </span>
      </div>
      {p.lines?.length > 0 && (
        <ul className="mt-2 ml-9 space-y-0.5">
          {p.lines.map((l, i) => (
            <li key={i} className="text-[11.5px] text-slate-600 flex items-center gap-1.5">
              <span className="truncate">{l.name}</span>
              <span className="tabular-nums text-slate-400 shrink-0">{l.old == null ? "added" : fmt(l.old)}</span>
              <ArrowRight size={10} className="text-slate-300 shrink-0" />
              <span className="tabular-nums font-semibold shrink-0">{l.new == null ? "removed" : fmt(l.new)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

const NOTE_ACTION = {
  added: ["Added", "bg-slate-50 border-slate-200 text-slate-600"],
  edited: ["Edited", "bg-[#EFF6FF] border-[#BFDBFE] text-[#1E5FEB]"],
  deleted: ["Deleted", "bg-rose-50 border-rose-200 text-rose-700"],
};

function NoteRow({ n }) {
  const [label, cls] = NOTE_ACTION[n.action] || NOTE_ACTION.added;
  return (
    <li className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`history-note-row-${n.action}`}>
      <div className="flex items-start gap-2 min-w-0">
        <span className="w-7 h-7 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">
          <NotePencil size={14} weight="bold" className="text-[#1E5FEB]" />
        </span>
        <div className="min-w-0">
          {n.action === "edited" ? (
            <>
              <div className="text-[12px] text-slate-400 line-through break-words" data-testid="history-note-old">{n.old_text}</div>
              <div className="text-[13px] text-slate-900 break-words" data-testid="history-note-new">{n.new_text}</div>
            </>
          ) : n.action === "deleted" ? (
            <div className="text-[13px] text-slate-500 line-through break-words" data-testid="history-note-old">{n.old_text}</div>
          ) : (
            <div className="text-[13px] text-slate-900 break-words">{n.new_text}</div>
          )}
          <div className="text-[11px] text-slate-500">{fmtInvoiceDate(n.at)} · {n.by || "Staff"}</div>
        </div>
      </div>
      <span className={`shrink-0 h-6 px-2 rounded-md border text-[10px] font-bold uppercase tracking-wider inline-flex items-center ${cls}`}>{label}</span>
    </li>
  );
}

const PROBLEM_LABELS = Object.fromEntries(PROBLEMS.map((p) => [p.id, p.label]));
const prettyWeb = (k) => k.replace(/^web_/, "").split("::").filter((p) => !p.startsWith("opt:"))
  .map((p) => p.replace(/[-_]+/g, " ")).join(" · ");
const checklistLabel = (k, labels) => labels[k] || PROBLEM_LABELS[k] || (k.startsWith("web_") ? prettyWeb(k)
  : k.startsWith("custom_") ? k.replace(/^custom_/, "").replace(/_/g, " ") : k.startsWith("cat_") ? "Custom issue" : k.replace(/_/g, " "));

function ChecklistRow({ c, meta }) {
  const Icon = c.ok ? CheckCircle : XCircle;
  return (
    <li className="px-4 py-3 flex items-start justify-between gap-3" data-testid="history-checklist-row">
      <div className="flex items-start gap-2 min-w-0">
        <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${c.ok ? "bg-emerald-50" : "bg-slate-100"}`}>
          <Icon size={14} weight="fill" className={c.ok ? "text-emerald-600" : "text-slate-400"} />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] text-slate-900 break-words capitalize">{checklistLabel(c.key, meta.labels || {})}</div>
          <div className="text-[11px] text-slate-500">{fmtInvoiceDate(meta.at)} · {meta.by}</div>
        </div>
      </div>
      <span className={`shrink-0 h-6 px-2 rounded-md border text-[10px] font-bold uppercase tracking-wider inline-flex items-center ${c.ok ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-500"}`}>
        {c.ok ? "Confirmed" : "Not confirmed"}
      </span>
    </li>
  );
}

export function MessageRow({ m }) {
  const Icon = m.channel === "email" ? EnvelopeSimple : ChatCircleText;
  return (
    <li className="px-4 py-3 flex items-start justify-between gap-3" data-testid="history-message-row">
      <div className="flex items-start gap-2 min-w-0">
        <span className="w-7 h-7 rounded-full bg-[#DBEAFE] flex items-center justify-center shrink-0">
          <Icon size={14} weight="bold" className="text-[#1E5FEB]" />
        </span>
        <div className="min-w-0">
          <div className="text-[13px] font-semibold text-slate-900 truncate">
            {KIND_LABELS[m.kind] || m.kind} <span className="font-normal text-slate-500">by {m.channel === "email" ? "email" : "SMS"}</span>
          </div>
          <div className="text-[11px] text-slate-500 truncate">{fmtInvoiceDate(m.at)} · {m.by}{m.to ? ` · ${m.to}` : ""}</div>
          {m.error && <div className="text-[11px] text-rose-600 mt-0.5">{m.error}</div>}
        </div>
      </div>
      <span className={`shrink-0 h-6 px-2 rounded-md border text-[10px] font-bold uppercase tracking-wider inline-flex items-center ${STATUS[m.status] || STATUS.failed}`}>
        {m.status === "not_configured" ? "Not set up" : m.status}
      </span>
    </li>
  );
}

export function useHistoryData({ invoiceId, ticketId, saleId, refreshKey }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams();
    if (invoiceId) params.set("invoice_id", invoiceId);
    if (ticketId) params.set("ticket_id", ticketId);
    if (saleId) params.set("sale_id", saleId);
    if (![...params.keys()].length) return;
    api.get(`/history?${params}`).then((r) => setData(r.data)).catch(() => setData({ price_changes: [], messages: [] }));
  }, [invoiceId, ticketId, saleId, refreshKey]);
  return data;
}

export function HistoryPanel({ invoiceId, ticketId, saleId, refreshKey, only }) {
  const data = useHistoryData({ invoiceId, ticketId, saleId, refreshKey });
  const [tab, setTab] = useState(only || "prices");

  if (!data) return null;
  const notes = data.notes || [];
  const checklist = data.checklist || [];
  const rows = tab === "prices" ? data.price_changes : tab === "notes" ? notes : tab === "checklist" ? checklist : data.messages;
  const TabBtn = ({ id, label, count }) => (
    <button onClick={() => setTab(id)} data-testid={`history-tab-${id}`}
            className={`h-8 px-3 rounded-lg text-[12px] font-semibold transition-colors ${tab === id ? "bg-[#EFF6FF] text-[#1E5FEB]" : "text-slate-500 hover:bg-slate-50"}`}>
      {label} <span className="tabular-nums opacity-70">{count}</span>
    </button>
  );
  return (
    <div data-testid="history-panel">
      <div className="flex items-center justify-between mb-2.5">
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500 flex items-center gap-1.5">
          <ClockCounterClockwise size={14} weight="bold" /> {only === "prices" ? "Price changes" : "History"}
        </div>
        {only ? (
          <span className="text-[11px] text-slate-400 tabular-nums">{rows.length}</span>
        ) : (
          <div className="flex flex-wrap justify-end gap-1">
            <TabBtn id="prices" label="Price changes" count={data.price_changes.length} />
            <TabBtn id="messages" label="Messages" count={data.messages.length} />
            {ticketId && <TabBtn id="notes" label="Notes" count={notes.length} />}
            {ticketId && <TabBtn id="checklist" label="Checklist" count={checklist.length} />}
          </div>
        )}
      </div>
      <div className="rounded-2xl border border-[#E5E7EB] bg-white overflow-hidden">
        {rows.length ? (
          <ul className="divide-y divide-slate-100 max-h-[320px] overflow-y-auto">
            {tab === "prices" ? rows.map((p) => <PriceRow key={p.id} p={p} />)
              : tab === "notes" ? rows.map((n) => <NoteRow key={n.id} n={n} />)
              : tab === "checklist" ? rows.map((c) => <ChecklistRow key={c.key} c={c} meta={data.checklist_meta || {}} />)
              : rows.map((m) => <MessageRow key={m.id} m={m} />)}
          </ul>
        ) : (
          <div className="px-4 py-5 text-[12.5px] text-slate-500" data-testid="history-empty">
            {tab === "prices" ? "No price changes yet." : tab === "notes" ? "No notes yet." : tab === "checklist" ? "No intake checklist on this ticket." : "No SMS or emails sent yet."}
          </div>
        )}
      </div>
    </div>
  );
}
